/* eslint-disable complexity */
import { DataPoint, PriceChangesChart, TsPoint } from '@toggle/design-system';
import {
  Entity,
  entityTagToCountry,
  formatTicker,
  PriceHistory,
} from '@toggle/toggle';
import { format, getDate, getMonth, getYear, isAfter } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { FlagIcon } from '~/components/flag-icon/FlagIcon';
import { InsightsLivePriceChange } from '~/components/insights-live-price-change/InsightsLivePriceChange';
import { NEW_YORK_TIMEZONE } from '~/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { useChartTheme } from '~/views/turbo-chart/hooks/use-chart-theme/useChartTheme';

import { MarketReactionLivePrice } from '../market-reaction-live-price/MarketReactionLivePrice';
import * as S from './MarketReactionCharts.styles';

export interface LineData {
  data: { time: string; value?: number }[];
  lastProportionChange?: number;
  prevCloseData?: PriceHistory[number];
  lastKnownPrice?: number;
  lastKnownDate?: string;
}

export interface MarketReactionChartProps {
  fromDateEST?: Date;
  toDateEST?: Date;
  lines: LineData[];
  entities?: Entity[];
}

export const MarketReactionChart = ({
  fromDateEST,
  toDateEST,
  lines,
  entities,
}: MarketReactionChartProps) => {
  const { t } = useTranslation(['insights']);
  const { colors } = useChartTheme(['soft']);
  const fillColors = {
    positive: colors.threshold.neutral.value,
    negative: colors.threshold.negative.value,
  };

  if (!lines.length || !entities || entities.length === 0 || !toDateEST) {
    return null;
  }

  const getVerticalLines = () => {
    if (!fromDateEST || !toDateEST) {
      return [];
    }

    return [
      {
        x: fromDateEST,
      },
      {
        x: new Date(
          getYear(fromDateEST),
          getMonth(fromDateEST),
          getDate(fromDateEST),
          16,
          0,
          0,
          0
        ),
        isDashed: true,
      },
      {
        x: new Date(
          getYear(toDateEST),
          getMonth(toDateEST),
          getDate(toDateEST),
          9,
          30,
          0,
          0
        ),
      },
      {
        x: toDateEST,
        isDashed: true,
      },
    ];
  };

  const nowEST = toZonedTime(new Date(), NEW_YORK_TIMEZONE);
  const isWindowComplete = isAfter(nowEST, toDateEST);

  const multiColors = colors.getMulti(lines.length);
  const chartLines = lines.map((line, i) => {
    const data: DataPoint[] = line.data.map(item => ({
      date: toZonedTime(new Date(item.time), NEW_YORK_TIMEZONE),
      value: item.value,
    }));
    const entity = entities[i];
    return {
      data,
      label: entity ? formatTicker(entity) : '',
      color:
        lines.length > 1 ? multiColors[i].value : colors.line.default.value,
    };
  });

  const isMultipleEntities = !!entities && entities.length > 1;

  const mergedValues = lines
    .flatMap(line => line.data.map(item => item.value))
    .filter((value): value is number => value !== undefined);

  const range =
    mergedValues.length > 0
      ? Math.max(...mergedValues) - Math.min(...mergedValues)
      : 0;
  const yAxisFormatType = isMultipleEntities
    ? range > 4
      ? 'percent'
      : 'percent1f'
    : 'default';
  const formattedEndDate = format(toDateEST, 'MMM d');
  const thresholdData =
    chartLines.length === 1
      ? chartLines[0].data.filter(
          (item): item is TsPoint => item.value !== undefined
        )
      : undefined;

  const firstLine = lines[0];
  return (
    <S.Container>
      <S.ChartTitleWrapper>
        <S.ChartTitle>
          <S.Title>{t('insights:catalyst.drawer.chartTitle')}</S.Title>
          <S.WindowDescription>
            <svg
              width={12}
              height={12}
              viewBox="0 0 40 40"
              fill={
                isWindowComplete ? 'var(--icon-soft)' : 'var(--icon-success)'
              }
            >
              <circle cx="20" cy="20" r="12" />
            </svg>
            <S.TitleText>
              {isWindowComplete
                ? t('insights:catalyst.drawer.windowComplete')
                : t('insights:catalyst.drawer.liveWindow')}
            </S.TitleText>
            <S.TitleText>
              {isWindowComplete
                ? t('insights:catalyst.drawer.finalAsOfDateClose', {
                    date: formattedEndDate,
                  })
                : t('insights:catalyst.drawer.endsAtClose', {
                    date: formattedEndDate,
                  })}
            </S.TitleText>
          </S.WindowDescription>
        </S.ChartTitle>
        {isMultipleEntities ? (
          firstLine.prevCloseData ? (
            <S.DateSpan>
              {t('analyze:analysis.sinceDateClose', {
                date: formatInTimeZone(
                  firstLine.prevCloseData.time,
                  'UTC',
                  'MMM d'
                ),
              })}
            </S.DateSpan>
          ) : null
        ) : (
          <MarketReactionLivePrice
            entity={entities[0]}
            isWindowComplete={isWindowComplete}
            eventProportionChange={firstLine.lastProportionChange}
            lastKnownPrice={firstLine.lastKnownPrice}
            lastKnownDate={firstLine.lastKnownDate}
            prevCloseData={firstLine.prevCloseData}
          />
        )}
      </S.ChartTitleWrapper>
      {isMultipleEntities && (
        <S.MultipleLegendWrapper>
          {entities.map((entity, index) => {
            const currentLine = lines[index];
            return (
              <S.MultipleLegend key={entity.tag}>
                <S.EtfLegendItem>
                  <S.IconWrapper>
                    <S.LegendItemVector $color={multiColors[index].value} />
                    <FlagIcon
                      countryCode={entityTagToCountry[entity.tag]}
                      variant="circular"
                      customSize={16}
                    />
                  </S.IconWrapper>
                  <S.EtfDetails>
                    <S.EtfName>{entity.name_short}</S.EtfName>
                    <S.EtfTag>{formatTicker(entity)}</S.EtfTag>
                  </S.EtfDetails>
                </S.EtfLegendItem>
                <InsightsLivePriceChange
                  entity={entity}
                  proportionChange={currentLine.lastProportionChange}
                  isWindowComplete={isWindowComplete}
                />
              </S.MultipleLegend>
            );
          })}
        </S.MultipleLegendWrapper>
      )}
      {!!lines.length && (
        <PriceChangesChart
          lines={chartLines}
          hasTooltip
          tooltipFormatType={isMultipleEntities ? 'percent2f' : 'default2f'}
          fillColors={fillColors}
          yAxisFormatType={yAxisFormatType}
          thresholdData={thresholdData}
          verticalLines={getVerticalLines()}
          tooltipSubtitle={
            isMultipleEntities && firstLine.prevCloseData
              ? t('analyze:analysis.sinceDateClose', {
                  date: format(firstLine.prevCloseData.time, 'MMM d'),
                })
              : undefined
          }
        />
      )}
    </S.Container>
  );
};
